import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Phone, MessageSquare, Mail } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import { ParentLayout } from "@/components/parent/ParentLayout";

interface SettingsResp {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  notificationPrefs: { email: boolean; whatsapp: boolean };
}

interface OptInRow {
  userId: string;
  studentName: string;
  grade: number;
  parentPhone: string | null;
  optedIn: boolean;
  lastReportSent: string | null;
}

export default function ParentSettings() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: settings, isLoading: loadingSettings } = useQuery<SettingsResp>({ queryKey: ["/api/parent/settings"] });
  const { data: optInRows, isLoading: loadingOptIn } = useQuery<OptInRow[]>({ queryKey: ["/api/parent/whatsapp-report-optin"] });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  useEffect(() => {
    if (settings) {
      setFirstName(settings.firstName ?? "");
      setLastName(settings.lastName ?? "");
    }
  }, [settings]);

  const saveProfile = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", "/api/parent/settings", { firstName: firstName.trim(), lastName: lastName.trim() });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/parent/settings"] });
      qc.invalidateQueries({ queryKey: ["/api/parent/me"] });
      toast({ title: "Profile saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const setPref = useMutation({
    mutationFn: async (next: Partial<SettingsResp["notificationPrefs"]>) => {
      await apiRequest("PATCH", "/api/parent/settings", { notificationPrefs: next });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/parent/settings"] }),
    onError: (e: any) => toast({ title: "Could not update", description: e.message, variant: "destructive" }),
  });

  const [phoneEdits, setPhoneEdits] = useState<Record<string, string>>({});
  const toggleReport = useMutation({
    mutationFn: async ({ childId, optIn, phone }: { childId: string; optIn?: boolean; phone?: string }) => {
      const body: any = {};
      if (typeof optIn === "boolean") body.optIn = optIn;
      if (phone !== undefined) body.phone = phone;
      const res = await apiRequest("PATCH", `/api/parent/whatsapp-report-optin/${childId}`, body);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/parent/whatsapp-report-optin"] }),
    onError: (e: any) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  return (
    <ParentLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-sm text-muted-foreground">Your profile, notification preferences, and weekly reports.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Profile</CardTitle>
            <CardDescription>Your name as it shows up across the dashboard.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingSettings ? (
              <Skeleton className="h-20" />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName">First name</Label>
                    <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="settings-first-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName">Last name</Label>
                    <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="settings-last-name" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Email (login)</Label>
                    <p className="text-sm">{settings?.email ?? "–"}</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Phone (WhatsApp)</Label>
                    <p className="text-sm">{settings?.phone ?? "–"}</p>
                  </div>
                </div>
                <Button onClick={() => saveProfile.mutate()} disabled={saveProfile.isPending}>
                  {saveProfile.isPending ? "Saving…" : "Save"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notification preferences</CardTitle>
            <CardDescription>Pick which channels we use for alerts, digests, and reminders.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingSettings || !settings ? (
              <Skeleton className="h-32" />
            ) : (
              <>
                <PrefRow
                  icon={Mail}
                  label="Email"
                  description="Billing receipts, dunning, weekly digests."
                  checked={settings.notificationPrefs.email}
                  onChange={(v) => setPref.mutate({ email: v })}
                  testId="pref-email"
                />
                <PrefRow
                  icon={MessageSquare}
                  label="WhatsApp"
                  description="Class reminders, weekly digests, urgent alerts."
                  checked={settings.notificationPrefs.whatsapp}
                  onChange={(v) => setPref.mutate({ whatsapp: v })}
                  testId="pref-whatsapp"
                />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Weekly WhatsApp progress reports</CardTitle>
            <CardDescription>Sunday-evening recap of each child's week, delivered via WhatsApp.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingOptIn ? (
              <div className="space-y-3">{[0,1].map((i) => <Skeleton key={i} className="h-20" />)}</div>
            ) : !optInRows || optInRows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No children linked.</p>
            ) : (
              optInRows.map((r) => (
                <div key={r.userId} className="rounded-md border p-4 space-y-3">
                  <div className="flex justify-between items-center gap-3 flex-wrap">
                    <div>
                      <p className="font-medium">{r.studentName}</p>
                      <p className="text-xs text-muted-foreground">Grade {r.grade}</p>
                    </div>
                    <Switch
                      checked={r.optedIn}
                      onCheckedChange={(v) => toggleReport.mutate({ childId: r.userId, optIn: v })}
                      disabled={toggleReport.isPending}
                      data-testid={`optin-switch-${r.userId}`}
                    />
                  </div>
                  {r.optedIn && (
                    <div className="space-y-2">
                      <div className="flex gap-2 items-end">
                        <div className="flex-1 space-y-1.5">
                          <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Phone className="w-3 h-3" /> WhatsApp number
                          </Label>
                          <Input
                            placeholder="+18761234567"
                            defaultValue={r.parentPhone ?? ""}
                            onChange={(e) => setPhoneEdits((p) => ({ ...p, [r.userId]: e.target.value }))}
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleReport.mutate({ childId: r.userId, phone: phoneEdits[r.userId] ?? r.parentPhone ?? "" })}
                          disabled={toggleReport.isPending}
                        >
                          Save
                        </Button>
                      </div>
                      {r.lastReportSent && (
                        <p className="text-xs text-muted-foreground">
                          Last report sent {formatDistanceToNow(new Date(r.lastReportSent), { addSuffix: true })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </ParentLayout>
  );
}

function PrefRow({ icon: Icon, label, description, checked, onChange, testId }: {
  icon: any; label: string; description: string; checked: boolean; onChange: (v: boolean) => void; testId: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-muted p-2 mt-0.5">
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="font-medium text-sm">{label}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}
